/**
 * Covering attorneys, and where they have appeared.
 *
 * Lloyd, Aug 21: *"a database for attorneys who have covered various courts"*, and an attorney who
 * covered Boerne Municipal *"would likely be able to cover Fair Oaks too, since they are only a few
 * miles from each other"*.
 *
 * ## Two lists, not one
 *
 * The attorney list is a directory: people the firm engages, whether or not they have a login. The
 * coverage list is history: who stood up where, on what day. They are shown separately because they
 * answer different questions, and because coverage cannot be edited — a row is an event, and the
 * record of it is the asset the firm never had.
 *
 * ## What it does not claim
 *
 * A suggestion carries the reason it was made — *"covered Boerne Municipal 4 times, same county"* —
 * and is shown as text a person can disagree with rather than a score. Proximity is approximated by
 * county until the court addresses are geocoded, which is coarser than "a few miles", so the basis
 * is printed rather than dressed up as distance.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CoverageApi, {
  CourtCoverageDto,
  CoveringAttorneyDto,
} from '../../api/CoverageApi';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  barNumber: '',
  willTravelMiles: '',
};

function ManagerCoverageView() {
  const [attorneys, setAttorneys] = useState<CoveringAttorneyDto[]>([]);
  const [coverage, setCoverage] = useState<CourtCoverageDto[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [people, history] = await Promise.all([
        CoverageApi.listAttorneys(includeInactive),
        CoverageApi.listCoverage(),
      ]);
      setAttorneys(people);
      setCoverage(history);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coverage');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await CoverageApi.createAttorney({
        CoveringAttorney_Name: form.name.trim(),
        CoveringAttorney_Email: form.email.trim() || null,
        CoveringAttorney_Phone: form.phone.trim() || null,
        CoveringAttorney_BarNumber: form.barNumber.trim() || null,
        CoveringAttorney_WillTravelMiles: form.willTravelMiles.trim()
          ? Number(form.willTravelMiles)
          : null,
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the attorney');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (attorney: CoveringAttorneyDto) => {
    setSaving(true);
    setError(null);
    try {
      await CoverageApi.setAttorneyActive(
        attorney.CoveringAttorney_guid,
        !attorney.CoveringAttorney_Active,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the attorney');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Covering attorneys
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Attorneys who cover settings for the firm, and the courts they have appeared at. Coverage is
        recorded from today onwards — the history was never kept before, so this list grows rather
        than starting complete.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
        }
        label="Show inactive"
        sx={{ mb: 1 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : attorneys.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No covering attorneys recorded yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {attorneys.map((attorney) => (
            <Paper
              key={attorney.CoveringAttorney_guid}
              variant="outlined"
              sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {attorney.CoveringAttorney_Name}
                  {!attorney.CoveringAttorney_Active && (
                    <Chip size="small" label="Inactive" sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {[
                    attorney.CoveringAttorney_Email,
                    attorney.CoveringAttorney_Phone,
                    attorney.CoveringAttorney_BarNumber
                      ? `Bar ${attorney.CoveringAttorney_BarNumber}`
                      : null,
                    // Only shown when they have actually said. An absent limit is not "no limit".
                    attorney.CoveringAttorney_WillTravelMiles !== null
                      ? `will travel ${attorney.CoveringAttorney_WillTravelMiles} miles`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No contact details on file'}
                </Typography>
              </Box>
              <Button size="small" disabled={saving} onClick={() => toggleActive(attorney)}>
                {attorney.CoveringAttorney_Active ? 'Deactivate' : 'Reactivate'}
              </Button>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add an attorney
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          label="Name"
          size="small"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextField
          label="Email"
          size="small"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <TextField
          label="Phone"
          size="small"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <TextField
          label="Bar number"
          size="small"
          value={form.barNumber}
          onChange={(e) => setForm({ ...form, barNumber: e.target.value })}
        />
        <TextField
          label="Will travel (miles)"
          size="small"
          type="number"
          sx={{ width: 160 }}
          value={form.willTravelMiles}
          onChange={(e) => setForm({ ...form, willTravelMiles: e.target.value })}
        />
        <Button
          variant="contained"
          disabled={saving || form.name.trim().length === 0}
          onClick={add}
        >
          Add
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        Appearances
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every recorded appearance. These cannot be edited or removed — a wrong entry is corrected by
        recording the right one and deactivating whoever was entered by mistake.
      </Typography>

      {coverage.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No appearances recorded yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {coverage.map((row) => (
            <Paper key={row.CourtCoverage_guid} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2">
                {row.attorneyName ?? 'Unknown attorney'} — {row.courtName ?? 'Unknown court'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.CourtCoverage_CoveredOn}
                {' · '}
                {/* A recovered record is evidence that coverage was arranged, not that it happened. */}
                {row.CourtCoverage_Source === 'mailbox'
                  ? 'recovered from court correspondence'
                  : 'entered by staff'}
                {row.CourtCoverage_Notes ? ` · ${row.CourtCoverage_Notes}` : ''}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default ManagerCoverageView;
